/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ApolloError } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";
import { Spacer, theme } from "@nordcloud/gnui";
import { ChartTableSortField } from "~/generated/graphql";
import {
  LoaderWrap,
  ErrorWrap,
  Pagination,
  Option,
  TableData,
  EstatePeriod,
  SortDirection,
} from "~/components";
import { SimpleError } from "~/tools";
import { EstateGroupingsTable } from "../EstateGroupings";
import { EstateRecordsLoader } from "../EstateRecordsLoader";
import { EstateGroupedParametersBar } from "../ParametersBar";

type Props = {
  loading: boolean;
  sortField: ChartTableSortField;
  sortDirection: SortDirection;
  sortOptions: Option[];
  onSortSelect: (value: ChartTableSortField) => void;
  onSortDirectionSelect: (direction: SortDirection) => void;
  tableCount: number;
  columns: ColumnDef<TableData>[];
  data: TableData[];
  period: EstatePeriod;
  firstColumnWidth?: string;
  jobId: string;
  error?: ApolloError | Error | SimpleError;
};

export function EstateRecordsGroupingTable({
  loading,
  tableCount,
  sortField,
  sortDirection,
  sortOptions,
  onSortDirectionSelect,
  onSortSelect,
  error,
  columns,
  data,
  firstColumnWidth,
  period,
  jobId,
}: Props) {
  return (
    <>
      <EstateGroupedParametersBar
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={sortOptions}
        disabled={loading}
        jobId={jobId}
        totalRecords={tableCount}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortDirectionSelect}
      />
      <LoaderWrap loading={loading} Component={EstateRecordsLoader}>
        <ErrorWrap error={error}>
          <div css={{ width: "100%", overflow: "scroll" }}>
            <EstateGroupingsTable
              period={period}
              columns={columns}
              data={data}
              firstColumnWidth={firstColumnWidth}
            />
          </div>
        </ErrorWrap>
      </LoaderWrap>
      <Spacer height={theme.spacing.spacing03} />
      <Pagination count={tableCount ?? 0} />
    </>
  );
}
